import { PagingResponse } from '@/@types/responses/common/PagingResponse'
import { AppConfig } from '@/app.config'
import authenticatedFetch from '@/authenticatedFetch'
import { InventoryItem } from '@/@types/responses/inventory_item/InventoryItem'
import { InventoryItemDetails } from '@/@types/responses/inventory_item/InventoryItemDetails'
import { CreateInventoryItemMessage } from '@/@types/messages/CreateInventoryItemMessage'
import { UpdateInventoryItemMessage } from '@/@types/messages/UpdateInventoryItemMessage'
import { MiscellaneousService } from './miscellaneous.service'
import { InventoryTypeFieldSearchValueMessage } from '@/@types/messages/InventoryTypeFieldSearchValueMessage'
import { ObjectCreatedResponse } from '@/@types/responses/common/ObjectCreatedResponse'

export class InventoryItemService {
  private appConfig: AppConfig
  private miscellaneousService: MiscellaneousService

  constructor(miscellaneousService: MiscellaneousService) {
    this.appConfig = new AppConfig()
    this.miscellaneousService = miscellaneousService
  }

  public createInventoryItem(createInventoryItemMessage: CreateInventoryItemMessage): Promise<ObjectCreatedResponse<string>> {
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(createInventoryItemMessage)
    }

    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/inventory-items`, this.miscellaneousService, requestOptions).then(async response => {
      const json = await response.json()
      return new ObjectCreatedResponse<string>(json.id)
    })
  }

  public deleteInventoryItem(id: number): Promise<void> {
    const requestOptions = {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' }
    }

    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/inventory-items/${id}`, this.miscellaneousService, requestOptions).then(() => {
      return Promise.resolve()
    })
  }

  public getInventoryItem(id: number): Promise<InventoryItemDetails> {
    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/inventory-items/${id}`, this.miscellaneousService).then(async response => {
      const json = await response.json()
      return new InventoryItemDetails(json.id, json.name, json.isCoreProduct, json.price, json.sku, json.totalQuantityOnHand, json.inventoryTypeId, json.inventoryTypeFieldValues, json.createdByUser, json.createdDate, json.lastModifiedByUser, json.lastModifiedDate)
    })
  }

  public searchInventoryItems(pageNumber: number, pageSize: number, sortField: string, ascending: boolean, inventoryTypeId: number, searchTerm: string, coreProduct: boolean, inventoryTypeFieldSearchValues: InventoryTypeFieldSearchValueMessage[]): Promise<PagingResponse<InventoryItem>> {
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        pageNumber,
        pageSize,
        sortField,
        ascending,
        inventoryTypeId,
        searchTerm,
        coreProduct,
        inventoryTypeFieldSearchValues
      })
    }

    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/inventory-items/search`, this.miscellaneousService, requestOptions).then(async response => {
      const json = await response.json()
      return json as PagingResponse<InventoryItem>
    })
  }

  public updateInventoryItem(inventoryItemId: number, updateInventoryItemMessage: UpdateInventoryItemMessage): Promise<void> {
    const requestOptions = {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updateInventoryItemMessage)
    }

    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/inventory-items/${inventoryItemId}`, this.miscellaneousService, requestOptions).then(() => {
      return Promise.resolve()
    })
  }
}
